import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import API from "../api";
import { useAuth } from "../contexts/AuthContext";
import API_BASE_URL from "../config";
import {
  Send, Search, MessageCircle, ArrowLeft,
  User, Loader2, CheckCheck, AlertCircle, Plus, Edit
} from "lucide-react";

// ─── touch helper – fixes Samsung Internet swallowing onClick ────────────────
const tap = (fn) => ({
  onClick: () => {
    document.activeElement?.blur();
    fn();
  },
  onTouchEnd: (e) => {
    e.preventDefault();
    e.stopPropagation();
    document.activeElement?.blur();
    setTimeout(fn, 50);
  },
});

// ─── Sidebar ──────────────────────────────────────────────────────────────────
function Sidebar({
  conversations, loading, selectedConv, search, setSearch,
  searching, searchResults, openOrCreateConversation,
  setSelectedConv, setIsMobileChat, formatTime, getAvatar, chatError,
}) {
  const isSearching = search.trim() || searching;

  return (
    <div className="msg-sidebar">
      <div className="msg-sidebar-head" style={{ background: '#2a3a1a', padding: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px', fontSize: '22px', fontWeight: 700, color: 'white' }}>
            <MessageCircle size={20} /> Messages
          </h2>
          <button style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#1D9E75', border: 'none', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
            <Edit size={16} />
          </button>
        </div>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <Search size={15} style={{ position: 'absolute', left: '12px', color: '#9ca3af', pointerEvents: 'none' }} />
          <input
            type="search"
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            enterKeyHint="search"
            style={{ width: '100%', padding: '10px 36px', background: 'white', border: '1px solid rgba(0,0,0,0.1)', borderRadius: '999px', color: '#1a1a1a', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
          />
          {search.trim() && (
            <button style={{ position: 'absolute', right: '10px', background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '16px' }} {...tap(() => setSearch(""))}>✕</button>
          )}
        </div>
      </div>

      {/* ── Error banner ── */}
      {chatError && (
        <div style={{
          margin: "8px 12px", padding: "10px 14px", background: "#fef2f2",
          border: "1px solid #fecaca", borderRadius: 10,
          display: "flex", alignItems: "center", gap: 8,
          fontSize: 13, color: "#dc2626",
        }}>
          <AlertCircle size={16} />
          <span>{chatError}</span>
        </div>
      )}

      {/* ── Search results shown INLINE ── */}
      {isSearching ? (
        <div className="msg-conv-list">
          {searching && (
            <div style={{ padding: '20px' }}>
              <div className="skeleton-item" style={{ height: '40px', background: 'var(--card)', borderRadius: '8px', marginBottom: '8px', animation: 'pulse 1.5s infinite' }}></div>
              <div className="skeleton-item" style={{ height: '40px', background: 'var(--card)', borderRadius: '8px', marginBottom: '8px', animation: 'pulse 1.5s infinite' }}></div>
              <div className="skeleton-item" style={{ height: '40px', background: 'var(--card)', borderRadius: '8px', marginBottom: '8px', animation: 'pulse 1.5s infinite' }}></div>
            </div>
          )}
          {!searching && searchResults.length === 0 && (
            <div className="msg-center"><p>No users found</p></div>
          )}
          {!searching && searchResults.map((u) => {
            const uid   = u.id ?? u._id;
            const uname = u.username || u.name || "Unknown";
            return (
              <button
                key={uid}
                className="msg-conv-item"
                {...tap(() => openOrCreateConversation(uid, uname, u.avatar))}
              >
                <div className="msg-av-wrap">
                  <img src={getAvatar(uname, u.avatar)} alt={uname} className="msg-av" />
                  <span className="online-indicator" style={{ position: 'absolute', bottom: '0', right: '0', width: '12px', height: '12px', background: '#10b981', border: '2px solid var(--background)', borderRadius: '50%' }}></span>
                </div>
                <div className="msg-conv-info">
                  <div className="msg-conv-top">
                    <span className="msg-conv-name">{uname}</span>
                  </div>
                  <p className="msg-conv-preview">Tap to start chatting</p>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        /* ── Conversation list ── */
        <div className="msg-conv-list">
          {loading && (
            <div style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', background: 'white', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#e5e7eb', animation: 'pulse 1.5s infinite' }}></div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <div style={{ width: '100px', height: '14px', background: '#e5e7eb', borderRadius: '4px', animation: 'pulse 1.5s infinite' }}></div>
                      <div style={{ width: '30px', height: '12px', background: '#e5e7eb', borderRadius: '4px', animation: 'pulse 1.5s infinite' }}></div>
                    </div>
                    <div style={{ width: '160px', height: '12px', background: '#e5e7eb', borderRadius: '4px', animation: 'pulse 1.5s infinite' }}></div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {!loading && conversations.length === 0 && (
            <div className="msg-center" style={{ marginTop: '20px', padding: '10px' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏔️</div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#1a2e1a', fontWeight: 'bold' }}>No messages yet</h3>
              <p style={{ fontSize: 13, color: "#6b7280", marginBottom: '24px' }}>Connect with other travelers in Uttarakhand.</p>
              
              <div style={{ width: '100%', textAlign: 'left' }}>
                <p style={{ fontSize: '12px', fontWeight: 'bold', textTransform: 'uppercase', color: 'var(--primary)', marginBottom: '12px' }}>Suggested travelers</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {["Aarti D.", "Rohan S.", "Priya M."].map((name, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px', background: 'var(--bg-secondary, hsl(var(--muted)))', borderRadius: '12px' }}>
                      <img src={getAvatar(name, "")} alt={name} style={{ width: '36px', height: '36px', borderRadius: '50%' }} />
                      <div style={{ flex: 1 }}>
                        <p style={{ margin: 0, fontSize: '14px', fontWeight: 'bold', color: 'var(--foreground)' }}>{name}</p>
                        <p style={{ margin: 0, fontSize: '12px', color: 'var(--muted-foreground)' }}>Planning Kedarnath</p>
                      </div>
                      <button style={{ background: 'var(--accent-primary, hsl(var(--primary)))', color: 'white', border: 'none', borderRadius: '8px', padding: '6px 12px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }}>Say Hi</button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {conversations.map((conv) => (
            <button
              key={conv.conversation_id}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: '12px',
                padding: '14px 16px', background: selectedConv?.conversation_id === conv.conversation_id ? '#e8f5f0' : 'white',
                border: 'none', borderBottom: '1px solid rgba(0,0,0,0.06)', cursor: 'pointer', textAlign: 'left',
                color: '#1a1a1a', marginBottom: 0,
              }}
              {...tap(() => { setSelectedConv(conv); setIsMobileChat(true); })}
            >
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <img src={getAvatar(conv.username, conv.avatar)} alt={conv.username} style={{ width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover', border: conv.unread_count > 0 ? '2px solid #1D9E75' : 'none', padding: conv.unread_count > 0 ? '2px' : '0' }} />
                {conv.unread_count > 0 && (
                  <span style={{ position: 'absolute', top: '0', right: '0', background: '#ef4444', color: 'white', fontSize: '10px', fontWeight: 'bold', minWidth: '16px', height: '16px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid white', padding: '0 4px' }}>{conv.unread_count}</span>
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                  <span style={{ fontWeight: 'bold', fontSize: '14px', color: '#1a2e1a' }}>{conv.username}</span>
                  <span style={{ fontSize: '11px', color: '#9ca3af' }}>{formatTime(conv.last_message_time)}</span>
                </div>
                <p style={{ margin: 0, fontSize: '13px', color: conv.unread_count > 0 ? '#1a2e1a' : '#6b7280', fontWeight: conv.unread_count > 0 ? 600 : 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {conv.last_message || "Start a conversation"}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── ChatWindow ───────────────────────────────────────────────────────────────
function ChatWindow({
  selectedConv, messages, newMessage, setNewMessage,
  sending, sendMessage, setIsMobileChat, setSelectedConv,
  messagesEndRef, userId, navigate, formatTime, getAvatar,
  authLoading, user
}) {
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  
  // mock typing indicator periodically if user stops typing (for creative effect)
  const isTyping = false;

  const quickReplies = [
    "Hi! I saw your post...",
    "Are you traveling to Kedarnath soon?",
    "Need any help with your itinerary?",
    "Do you know the current road condition?"
  ];

  return (
    <div className="msg-chat-inner">
      <div className="msg-chat-header">
        <button
          className="msg-back-btn"
          {...tap(() => { setIsMobileChat(false); setSelectedConv(null); })}
        >
          <ArrowLeft size={18} />
        </button>
        {selectedConv && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
            <img
              src={getAvatar(selectedConv.username, selectedConv.avatar)}
              alt={selectedConv.username}
              className="msg-av-sm"
            />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <button
                className="msg-chat-uname"
                {...tap(() => navigate(`/profile/${selectedConv.other_user_id}`))}
                style={{ textAlign: 'left', padding: 0 }}
              >
                {selectedConv.username}
              </button>
              <span style={{ fontSize: '11px', color: 'var(--primary)', fontWeight: 'bold' }}>Online</span>
            </div>
          </div>
        )}
      </div>

      <div className="msg-chat-body">
        {messages.length === 0 && (
          <div className="msg-center" style={{ marginTop: 'auto', marginBottom: 'auto' }}>
            <div style={{ background: 'var(--bg-secondary, hsl(var(--muted)))', padding: '24px', borderRadius: '16px', maxWidth: '300px' }}>
              <h4 style={{ margin: '0 0 12px 0', color: 'var(--foreground)' }}>Say Hi to {selectedConv.username}</h4>
              <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: 'var(--muted-foreground)' }}>Here are some travel-themed conversation starters:</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {quickReplies.map((qr, i) => (
                  <button key={i} onClick={() => { setNewMessage(qr); setShowQuickReplies(false); }} style={{ background: 'var(--background)', border: '1px solid var(--border)', borderRadius: '20px', padding: '8px 16px', fontSize: '13px', color: 'var(--primary)', cursor: 'pointer', textAlign: 'left' }}>
                    {qr}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
        {messages.map((msg) => {
          const isMine = msg.sender_id === userId;
          return (
            <div key={msg.id} className={`msg-bubble-wrap ${isMine ? "mine" : "theirs"}`} style={{ flexDirection: 'column', alignItems: isMine ? 'flex-end' : 'flex-start' }}>
               <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', flexDirection: isMine ? 'row-reverse' : 'row' }}>
                  {!isMine && (
                    <img
                      src={getAvatar(msg.sender_username, msg.sender_avatar)}
                      alt={msg.sender_username}
                      className="msg-bubble-av"
                    />
                  )}
                  <div className={`msg-bubble ${isMine ? "bubble-mine" : "bubble-theirs"}`}>
                    <p>{msg.content}</p>
                  </div>
               </div>
               <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '2px', marginLeft: isMine ? 0 : '36px', marginRight: isMine ? '4px' : 0, display: 'flex', alignItems: 'center', gap: '4px' }}>
                 {formatTime(msg.created_at)}
                 {isMine && msg.is_read && <CheckCheck size={12} style={{ color: '#1D9E75' }} />}
               </div>
            </div>
          );
        })}
        {isTyping && (
          <div className="msg-bubble-wrap theirs">
             <img src={getAvatar(selectedConv.username, selectedConv.avatar)} className="msg-bubble-av" alt="avatar"/>
             <div className="msg-bubble bubble-theirs typing-indicator" style={{ display: 'flex', gap: '4px', padding: '12px 16px', alignItems: 'center' }}>
               <span style={{ width: '6px', height: '6px', background: 'var(--muted-foreground)', borderRadius: '50%', animation: 'bounce 1.4s infinite ease-in-out both' }}></span>
               <span style={{ width: '6px', height: '6px', background: 'var(--muted-foreground)', borderRadius: '50%', animation: 'bounce 1.4s infinite ease-in-out both', animationDelay: '0.2s' }}></span>
               <span style={{ width: '6px', height: '6px', background: 'var(--muted-foreground)', borderRadius: '50%', animation: 'bounce 1.4s infinite ease-in-out both', animationDelay: '0.4s' }}></span>
             </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div style={{ position: 'relative' }}>
        {showQuickReplies && (
          <div style={{ position: 'absolute', bottom: '100%', left: '16px', background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '8px', display: 'flex', flexDirection: 'column', gap: '4px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', marginBottom: '8px', zIndex: 10 }}>
            {quickReplies.map((qr, i) => (
               <button key={i} onClick={() => { setNewMessage(qr); setShowQuickReplies(false); }} style={{ background: 'none', border: 'none', padding: '8px 12px', fontSize: '13px', color: 'var(--foreground)', cursor: 'pointer', textAlign: 'left', borderRadius: '6px' }}>
                 {qr}
               </button>
            ))}
          </div>
        )}
        <form className="msg-input-form" onSubmit={(e) => { sendMessage(e); setShowQuickReplies(false); }}>
          <button type="button" onClick={() => setShowQuickReplies(!showQuickReplies)} style={{ background: 'var(--bg-secondary, hsl(var(--muted)))', border: 'none', color: 'var(--foreground)', cursor: 'pointer', padding: '8px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Plus size={20} />
          </button>
          <input
            className="msg-input"
            type="text"
            placeholder="Type a message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            autoComplete="off"
            onFocus={() => setShowQuickReplies(false)}
          />
          <button
            type="submit"
            className="msg-send-btn"
            disabled={!newMessage.trim() || sending || authLoading || !user}
          >
            {sending ? <Loader2 size={18} className="spin" /> : <Send size={18} style={{ marginLeft: '-2px' }} />}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function Messages() {
  const { user, loading: authLoading } = useAuth();
  const navigate       = useNavigate();
  const [searchParams] = useSearchParams();

  const [conversations,  setConversations]  = useState([]);
  const [selectedConv,   setSelectedConv]   = useState(null);
  const [messages,       setMessages]       = useState([]);
  const [newMessage,     setNewMessage]     = useState("");
  const [loading,        setLoading]        = useState(true);
  const [sending,        setSending]        = useState(false);
  const [search,         setSearch]         = useState("");
  const [searchResults,  setSearchResults]  = useState([]);
  const [searching,      setSearching]      = useState(false);
  const [isMobileChat,   setIsMobileChat]   = useState(false);
  const [chatError,      setChatError]      = useState("");

  const messagesEndRef = useRef(null);
  const pollRef        = useRef(null);
  const userId         = user?.id ?? user?._id;

  const getAvatar = (name, avatar) => {
    if (avatar) return avatar;
    const initial = (name || "?").charAt(0).toUpperCase();
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(initial)}&background=2D6A4F&color=fff&size=64`;
  };

  const formatTime = (ts) => {
    if (!ts) return "";
    const d = new Date(ts);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return "now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  };

  const fetchConversations = useCallback(async () => {
    try {
      const res = await API.get(`/messages/conversations`);
      setConversations(res.data);
    } catch (err) {
      console.error("fetchConversations", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  useEffect(() => {
    const withUser = searchParams.get("with");
    if (withUser) openOrCreateConversation(withUser);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (!selectedConv) return;
    const load = async () => {
      try {
        const res = await API.get(`/messages/${selectedConv.conversation_id}`);
        setMessages(res.data);
      } catch (err) { console.error(err); }
    };
    load();
    pollRef.current = setInterval(load, 5000);
    return () => clearInterval(pollRef.current);
  }, [selectedConv]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (!search.trim()) { setSearchResults([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const cleanQuery = search.replace('@', '').trim();
        const res = await API.get(`/users/search?q=${encodeURIComponent(cleanQuery)}`);
        setSearchResults(res.data.filter((u) => (u.id ?? u._id) !== userId));
      } catch { setSearchResults([]); }
      finally   { setSearching(false); }
    }, 400);
    return () => clearTimeout(timer);
  }, [search, userId]);

  const openOrCreateConversation = async (otherUserId, knownUsername, knownAvatar) => {
    if (!otherUserId) return;
    setChatError("");
    try {
      const res    = await API.post(
        `/messages/conversation`,
        { other_user_id: otherUserId }
      );
      const convId = res.data.conversation_id;
      let conv     = conversations.find((c) => c.conversation_id === convId);

      if (!conv) {
        let username = knownUsername;
        let avatar   = knownAvatar;
        if (!username) {
          try {
            const r = await API.get(`/users/${otherUserId}`);
            username = r.data.username || r.data.name || "Unknown";
            avatar   = r.data.avatar;
          } catch { username = "Unknown"; }
        }
        conv = { conversation_id: convId, other_user_id: otherUserId, username, avatar, last_message: null, unread_count: 0 };
        setConversations((prev) => [conv, ...prev]);
      }

      setSelectedConv(conv);
      setSearch("");
      setSearchResults([]);
      setIsMobileChat(true);
    } catch (err) {
      console.error("openOrCreateConversation", err);
      const status = err?.response?.status;
      const msg    = err?.response?.data?.detail || err?.response?.data?.message || "";
      if (status === 401) {
        setChatError("Please log in again to send messages.");
      } else if (status === 403) {
        setChatError("You don't have permission to message this user.");
      } else if (status === 404) {
        setChatError("User not found.");
      } else if (status >= 500) {
        setChatError("Server error. Please try again in a moment.");
      } else if (!navigator.onLine) {
        setChatError("No internet connection. Check your network.");
      } else {
        setChatError(msg || "Could not open chat. Please try again.");
      }
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    const content = newMessage.trim();
    if (!content || !selectedConv) return;

    // Optimistic UI update
    const optimisticMsg = {
      id: `temp-${Date.now()}`,
      content,
      sender_id: userId,
      sender_username: user?.username || user?.name || "Me",
      created_at: new Date().toISOString(),
      is_read: false
    };
    
    setMessages(prev => [...prev, optimisticMsg]);
    setNewMessage("");
    
    setSending(true);
    try {
      await API.post(
        `/messages/${selectedConv.conversation_id}`,
        { content }
      );
      const res = await API.get(`/messages/${selectedConv.conversation_id}`);
      setMessages(res.data);
    } catch (err) {
      console.error("sendMessage", err);
      // Revert optimistic update on error by fetching real messages
      const res = await API.get(`/messages/${selectedConv.conversation_id}`);
      setMessages(res.data);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <style>{`
        .msg-wrap { display:flex; height:calc(100dvh - 56px); overflow:hidden; background:#f2f0eb; color:#1a1a1a; }
        .msg-sidebar { width:320px; flex-shrink:0; border-right:1px solid rgba(0,0,0,0.06); display:flex; flex-direction:column; overflow:hidden; background:#f2f0eb; }
        .msg-sidebar-head { border-bottom:1px solid rgba(0,0,0,0.06); }
        .msg-conv-list { flex:1; overflow-y:auto; overscroll-behavior-y: contain; padding:8px; display:flex; flex-direction:column; gap:8px; }
        .msg-av-sm { width:32px; height:32px; border-radius:50%; object-fit:cover; }
        .msg-chat { flex:1; display:flex; flex-direction:column; overflow:hidden; }
        .msg-chat-inner { display:flex; flex-direction:column; height:100%; }
        .msg-chat-header { display:flex; align-items:center; gap:10px; padding:12px 16px; border-bottom:1px solid rgba(0,0,0,0.06); background:white; }
        .msg-back-btn { background:none; border:none; color:#1a1a1a; cursor:pointer; display:flex; align-items:center; padding:4px; border-radius:8px; }
        .msg-back-btn:hover { background:rgba(0,0,0,0.05); }
        .msg-chat-uname { background:none; border:none; color:#1a1a1a; font-weight:600; font-size:15px; cursor:pointer; }
        .msg-chat-body { flex:1; overflow-y:auto; padding:16px; display:flex; flex-direction:column; gap:8px; background:#f2f0eb; padding-bottom:80px; }
        .msg-bubble-wrap { display:flex; align-items:flex-end; gap:8px; margin-bottom: 8px; }
        .msg-bubble-wrap.mine { flex-direction:row-reverse; }
        .msg-bubble-av { width:28px; height:28px; border-radius:50%; object-fit:cover; flex-shrink:0; }
        .msg-bubble { max-width:75%; padding:10px 14px; font-size:14px; line-height:1.4; display: flex; flex-direction: column; }
        .bubble-mine { background:#1a3a1a; color:white; border-radius:18px 18px 4px 18px; }
        .bubble-theirs { background:#f2f0eb; color:#1a2e1a; border-radius:18px 18px 18px 4px; }
        .msg-bubble p { margin:0; }
        .msg-input-form { display:flex; align-items:center; gap:8px; padding:10px 16px; padding-bottom:calc(10px + env(safe-area-inset-bottom)); border-top:1px solid rgba(0,0,0,0.06); background:white; }
        .msg-input { flex:1; padding:12px 20px; background:#f2f0eb; border:none; border-radius:999px; color:#1a2e1a; font-size:14px; outline:none; }
        .msg-send-btn { width:40px; height:40px; border-radius:50%; background:#1D9E75; color:white; border:none; cursor:pointer; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .msg-send-btn:disabled { opacity:0.5; }
        .msg-center { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:40px 20px; color:#9ca3af; gap:8px; text-align:center; }
        .msg-empty { flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center; color:#9ca3af; gap:12px; }
        .spin { animation:spin 1s linear infinite; }
        @keyframes spin { to { transform:rotate(360deg); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: .5; } }
        @keyframes bounce { 0%, 80%, 100% { transform: translateY(0); } 40% { transform: translateY(-4px); } }
        @media (max-width:640px) {
          .msg-sidebar { width:100%; display:${isMobileChat ? "none" : "flex"}; }
          .msg-chat { display:${isMobileChat ? "flex" : "none"}; }
        }
      `}</style>

      <div className="msg-wrap">
        <Sidebar
          conversations={conversations}
          loading={loading}
          selectedConv={selectedConv}
          search={search}
          setSearch={setSearch}
          searching={searching}
          searchResults={searchResults}
          openOrCreateConversation={openOrCreateConversation}
          setSelectedConv={setSelectedConv}
          setIsMobileChat={setIsMobileChat}
          formatTime={formatTime}
          getAvatar={getAvatar}
          chatError={chatError}
        />

        <div className="msg-chat">
          {selectedConv ? (
            <ChatWindow
              selectedConv={selectedConv}
              messages={messages}
              newMessage={newMessage}
              setNewMessage={setNewMessage}
              sending={sending}
              sendMessage={sendMessage}
              setIsMobileChat={setIsMobileChat}
              setSelectedConv={setSelectedConv}
              messagesEndRef={messagesEndRef}
              userId={userId}
              navigate={navigate}
              formatTime={formatTime}
              getAvatar={getAvatar}
              authLoading={authLoading}
              user={user}
            />
          ) : (
            <div className="msg-empty">
              <MessageCircle size={48} opacity={0.3} />
              <p>Select a conversation or search for a user</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
